import { friendlyApiErrorMessage } from 'src/app/services/api-error-notifier.service';
import { ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { Subscription } from 'rxjs';
import { ApiService } from '../../../../../services/api.service';
import { FilterService } from '../../../../../services/filter.service';

interface JourneyStage {
  referral_source: string;
  du_patient_id: string;
  du_status_group: string;
  status_date: string | null;
  seq_no: number;
  no_of_days?: number | null;
  stage_status?: 'success' | 'skipped' | 'in progress' | 'not reached';
}

@Component({
    selector: 'app-status-journey-view',
    templateUrl: './status-journey-view.component.html',
    styleUrls: ['./status-journey-view.component.scss'],
    standalone: false
})
export class StatusJourneyViewComponent implements ICellRendererAngularComp {
  
  @ViewChild('root', { static: false }) root!: ElementRef;

  reqSubcription: Subscription[] = [];
  loader = true;
  error_name: any;
  data: any = []
  params: any;
  groupedJourneys: {
    du_patient_id: number;
    referral_source: string;
    stages: JourneyStage[];
  }[] = [];

  constructor(
    private sanitizer: DomSanitizer,
    private apiService: ApiService,
    private filterService: FilterService,
    private cdr: ChangeDetectorRef,
  ) { }

  // Required by AG Grid
  agInit(params: any): void {
    this.data = []
    this.params = params;
    this.loader = true;
    this.loadApiData();    
  }

  ngAfterViewInit(): void {
    // setTimeout(() => {
    //   const height = this.root?.nativeElement?.scrollHeight;
    //   this.params.node.setRowHeight(height ? (height + 100) : 300);
    //   this.params.api.onRowHeightChanged();
    // });
  }

  // Optional: refresh is used if the grid asks to re-render
  refresh(params: any): boolean {
    this.params = params;
    return true;
  }

  processGroupedJourneys(data: JourneyStage[]) {
    if (!data?.length) return [];

    const grouped = data.reduce((acc: any, item: any) => {
      const referral = item.referral_source || ''; // 👈 normalize
      const key = `${item.du_patient_id}__${referral}`;

      if (!acc[key]) {
        acc[key] = {
          du_patient_id: item.du_patient_id,
          referral_source: referral || null,
          stages: []
        };
      }

      acc[key].stages.push({
        ...item,
        du_status_group: item.patient_status_grp
      });

      return acc;
    }, {});

    return Object.values(grouped).map((group: any) => ({
      du_patient_id: group.du_patient_id,
      referral_source: group.referral_source, // may be null
      stages: this.processJourney(group.stages)
    }));
  }

  processJourney(data: JourneyStage[]): JourneyStage[] {
    if (!data?.length) return [];

    // 1️⃣ Sort by sequence
    const sorted = [...data].sort((a, b) => a.seq_no - b.seq_no);

    // 2️⃣ Find last index that has a status_date
    const lastReachedIndex = [...sorted]
      .map(s => !!s.status_date)
      .lastIndexOf(true);

    return sorted.map((current, index) => {
      const hasDate = !!current.status_date;
      const hasFutureDate =
        sorted.slice(index + 1).some(s => !!s.status_date);

      let stage_status: JourneyStage['stage_status'];

      if (hasDate) {
        if (index === lastReachedIndex) {
          stage_status = 'in progress';
        } else {
          stage_status = 'success';
        }
      } else {
        if (hasFutureDate) {
          stage_status = 'skipped';
        } else {
          stage_status = 'not reached';
        }
      }

      /* -----------------------------
       * NO OF DAYS (only between
       * consecutive reached stages)
       * ----------------------------- */
      let no_of_days: number | null = null;

      if (
        hasDate &&
        index > 0 &&
        sorted[index - 1].status_date
      ) {
        const curr = new Date(current.status_date!);
        const prev = new Date(sorted[index - 1].status_date!);
        const diffMs = curr.getTime() - prev.getTime();
        no_of_days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      }

      return {
        ...current,
        no_of_days,
        stage_status
      };
    });
  }

  getStageClasses(stage: JourneyStage, stages: JourneyStage[], i: number) {
    return {
      'icon-success': stage.stage_status === 'success',
      'icon-inprogress': stage.stage_status === 'in progress',
      'line-success': stages[i + 1]?.stage_status === 'success',
      'line-inprogress': stages[i + 1]?.stage_status === 'in progress'
    };
  }

  getLineLabel(stages: JourneyStage[], i: number) {
    if (stages[i + 1]?.stage_status === 'in progress') {
      return 'In progress';
    }
    if (stages[i + 1]?.no_of_days != null) {
      return `${stages[i + 1]?.no_of_days} days`;
    }
    return null;
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'success':
        return 'is-success';
      case 'in progress':
        return 'is-progress';
      default:
        return 'is-grey';
    }
  }

  getLineClass(current: any, next: any): string {
    if (!next) return 'line-grey';

    if (current.stage_status === 'success') {
      return 'line-success';
    }

    if (current.stage_status === 'in progress') {
      return 'line-progress';
    }

    return 'line-grey';
  }

  SUCCESS_ICON = `
<svg width="16" height="16" viewBox="0 0 12 12" fill="none"
 xmlns="http://www.w3.org/2000/svg">
  <path d="M10.75 5.29286V5.75286C10.7494 6.83107 10.4003 7.8802
  9.75467 8.74377C9.10908 9.60735 8.20164 10.2391
  7.16768 10.5448C6.13372 10.8505 5.02863 10.8138
  4.01724 10.4402C3.00584 10.0665 2.14233 9.37591
  1.55548 8.4714C0.968633 7.56688 0.689896 6.49689
  0.760838 5.42102C0.831781 4.34514 1.2486 3.32103
  1.94914 2.5014C2.64968 1.68177 3.59639 1.11055
  4.64809 0.872935C5.6998 0.635317 6.80013 0.74403
  7.785 1.18286" stroke="white" stroke-width="1.5"
  stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M10.75 1.75287L5.75 6.75787L4.25 5.25787"
  stroke="white" stroke-width="1.5"
  stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`;

  IN_PROGRESS_ICON = `
<svg width="16" height="16" viewBox="0 0 12 12" fill="none"
 xmlns="http://www.w3.org/2000/svg">
  <path d="M5.75 10.75C8.51142 10.75 10.75 8.51142
  10.75 5.75C10.75 2.98858 8.51142 0.75
  5.75 0.75C2.98858 0.75 0.75 2.98858
  0.75 5.75C0.75 8.51142 2.98858 10.75
  5.75 10.75Z" stroke="white" stroke-width="1.5"/>
  <path d="M5.75 2.75V5.75L7.75 6.75"
  stroke="white" stroke-width="1.5"/>
</svg>
`;

  SKIPPED_ICON = `
<svg width="14" height="11" viewBox="0 0 10 7" fill="none"
 xmlns="http://www.w3.org/2000/svg">
  <path d="M6.25 5.75L8.75 3.25L6.25 0.75"
  stroke="#8A98AB" stroke-width="1.5"/>
  <path d="M0.75 5.75L3.25 3.25L0.75 0.75"
  stroke="#8A98AB" stroke-width="1.5"/>
</svg>
`;

  NOT_REACHED_ICON = `
<svg width="16" height="16" viewBox="0 0 12 12" fill="none"
 xmlns="http://www.w3.org/2000/svg">
  <path d="M5.75 0.75V2.75" stroke="#8A98AB" stroke-width="1.5"/>
  <path d="M5.75 8.75V10.75" stroke="#8A98AB"
  stroke-opacity="0.5" stroke-width="1.5"/>
  <path d="M2.21484 2.215L3.62984 3.63"
  stroke="#8A98AB" stroke-width="1.5"/>
  <path d="M7.87109 7.87L9.28609 9.285"
  stroke="#8A98AB" stroke-opacity="0.3" stroke-width="1.5"/>
  <path d="M0.75 5.75H2.75" stroke="#8A98AB" stroke-width="1.5"/>
  <path d="M8.75 5.75H10.75" stroke="#8A98AB" stroke-width="1.5"/>
  <path d="M2.21484 9.285L3.62984 7.87"
  stroke="#8A98AB" stroke-width="1.5"/>
  <path d="M7.87109 3.63L9.28609 2.215"
  stroke="#8A98AB" stroke-width="1.5"/>
</svg>
`;

  iconMap: Record<string, SafeHtml> = {
    'success': this.sanitizer.bypassSecurityTrustHtml(this.SUCCESS_ICON),
    'in progress': this.sanitizer.bypassSecurityTrustHtml(this.IN_PROGRESS_ICON),
    'skipped': this.sanitizer.bypassSecurityTrustHtml(this.SKIPPED_ICON),
    'not reached': this.sanitizer.bypassSecurityTrustHtml(this.NOT_REACHED_ICON),
  };

  getStageIcon(status: string): SafeHtml {
    return this.iconMap[status];
  }

  loadApiData() {
    this.loader = true;
    let designerVar = this.params?.['designerCellRendererParams'] || {};

    if(this.filterService.isEmptyObject(this.filterService.baseQuery) == false) {
      let obj:any = {"api_key" : designerVar?.apiKey == 100926 ? 100931 : (designerVar?.apiKey || 100931)}

      let query1 = Object.assign({}, this.filterService.baseQuery, obj)
      query1["report_typ"] = this.filterService.report_type || 'D';
      query1["soc_state"] = this.params?.data?.soc_state;
      query1["state_cd"] = this.params?.data?.soc_state;
      query1["territory_dim_key"] = this.params?.data?.territory_key;
      query1["oracle_account_id"] = this.params?.data?.oracle_account_id;

      this.error_name = '';
      let endpoint = designerVar?.endpoint;

      this.reqSubcription.push(
        this.apiService.post(`${endpoint ? endpoint : this.apiService.executionUrl}`, query1).subscribe((res:any)=> {
          let resData:any = res == null ? [] : res;
          if(resData.length>0) {
            this.data = resData;
            this.groupedJourneys = this.processGroupedJourneys(this.data);
          } else {
            this.data = [];
            this.error_name = 'There is no data for this time period';
          }

          // Resize the detail row
          setTimeout(() => {
            const height = this.root?.nativeElement?.scrollHeight;
            this.params.node.setRowHeight(height ? (height + 70) : 300);
            this.params.api.onRowHeightChanged();
          });
          this.loader = false;
          this.cdr.detectChanges()
        },(err:any)=>{
          this.loader = false;
          this.data = [];
          this.error_name =   friendlyApiErrorMessage(err);
          this.cdr.detectChanges()
        })
      )
    }
  }
}
